import { Button } from "@gigpro/ui/Button";
import { Checkbox } from "@gigpro/ui/Checkbox";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Form, FormField, type SubmitHandler } from "@gigpro/ui/Form";
import { InputFile } from "@gigpro/ui/Form/InputFile";
import { Select, SelectItem } from "@gigpro/ui/Form/Select";
import { Textarea } from "@gigpro/ui/Form/Textarea";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { InfoOutline } from "@gigpro/ui/Icons";
import { Label } from "@gigpro/ui/Label";
import { Separator } from "@gigpro/ui/Separator";
import { Switch } from "@gigpro/ui/Switch";
import { Tooltip } from "@gigpro/ui/Tooltip";
import { ApiError } from "@sidegigllc/api";
import {
  useSkillsList,
  useStafferGigsTemplatesPartialUpdate,
  useStafferGigsValidateWithTransform,
  type ForManagerLocationDetail,
} from "@sidegigllc/api/client";
import { useGigTemplates } from "api/queries/gigPostTemplates";
import { useShiftCostsValidation } from "api/queries/stafferGigs";
import type { GigPostPayload } from "api/resources/gigs";
import type { GigTemplatePayload } from "api/resources/templates";
import { TipPolicyField } from "domains/postGig/components/TipPolicyField";
import { INPUT_FILE_ACCEPT, useUploadAttachment } from "domains/postGig/hooks/useUploadAttachment";
import {
  tipPolicyRefinement,
  type Pro,
  proPostSchema,
  type SectionShift,
} from "domains/postGig/providers/PostGigData/types";
import { useFeatureFlag } from "providers/LaunchDarkly";
import React from "react";
import { useFormState, useWatch } from "react-hook-form";
import type { ModalBaseProps } from "types/utils";
import { isAutoSelectExcludedPosition } from "utils/GigUtil";
import { isWageValid, MAX_WAGE } from "utils/validation";
import * as z from "zod/mini";
import { WagePickerContainer, type WagePickerContainerProps } from "../WagePicker";
import { AccessoriesRequirementsFlagged } from "./AccessoriesRequirementsFlagged";
import { getDefault as getDefaults } from "./helpers";
import { extraPositions, worldCupPositions } from "./positions";
import { RequirementsFlagged } from "./RequirementsFlagged";
import { SaveTemplateDialog } from "./SaveTemplateDialog";

type FormDialogProps = ModalBaseProps & {
  shift: SectionShift;
  location: ForManagerLocationDetail;
  template?: Pro;
  flags?: {
    hideWage?: boolean;
    showAutoSelect?: boolean;
  };
  /** From the Post page's "Change details": the edit is this post's only, and no saved position is written. */
  postOnly?: boolean;
  /** The Post page's Recommended (last time's wage): offered, and opened on when the wage is it. Classic passes none. */
  recommendedWage?: number;
  onSuccess: (values: Pro) => void;
};

export function FormDialog(props: Readonly<FormDialogProps>) {
  return (
    <Dialog open={props.open} onClose={props.onClose} bottomSheet>
      <DialogBody {...props} />
    </Dialog>
  );
}

const formSchema = z.extend(z.omit(proPostSchema, { attachments: true }), {
  attachment_ids: z.optional(z.array(z.string())),
});

type FormValues = z.infer<typeof formSchema>;
const formId = "pro-request-form";

function DialogBody({
  onClose,
  onSuccess,
  shift,
  location,
  template,
  flags,
  postOnly,
  recommendedWage,
}: Readonly<FormDialogProps>) {
  const [currentWageOption, setCurrentWageOption] = React.useState<WagePickerContainerProps["value"]>();
  const isExtraPositions = useFeatureFlag("extra-positions");
  // World Cup account positions (CORE-5564) — independent flag from Fever's
  // ``extra-positions`` so each org's segment targeting stays isolated. The
  // Set lives in ./positions alongside ``extraPositions``.
  const isWorldCupPositions = useFeatureFlag("world-cup-positions");
  // Off: the tips question is hidden and so never required, whatever the skill's `tips_required` says.
  const isIncludesTipsEnabled = useFeatureFlag("fe-shift-includes-tips");

  const { data: allSkills } = useSkillsList(
    { include_defaults: true, include_recommendations: true, query: "ordered_by_popularity" },
    { query: { staleTime: Number.POSITIVE_INFINITY } },
  );
  const skills = (allSkills ?? [])
    .filter((skill) => {
      if (extraPositions.has(skill.name) && !isExtraPositions) return false;
      if (worldCupPositions.has(skill.name) && !isWorldCupPositions) return false;
      return true;
    })
    .toSorted((a, b) => a.name.localeCompare(b.name));
  const { invalidate: invalidateTemplates } = useGigTemplates(
    { location_id: location.id },
    { query: { staleTime: Number.POSITIVE_INFINITY } },
  );

  const { mutateAsync: fetchCosts } = useShiftCostsValidation();
  const { mutateAsync: validateGig } = useStafferGigsValidateWithTransform();
  const { mutateAsync: updateTemplate } = useStafferGigsTemplatesPartialUpdate();

  const [saveTemplate, setSaveTemplate] = React.useState(false);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = React.useState(false);
  const [templateToSave, setTemplateToSave] = React.useState<FormValues>();

  const form = useZodForm({
    schema: formSchema,
    schemaTransform: (schema, data, context) => {
      if (!context || !data.skill_id) return schema;
      const skill = context.find(({ id }) => id === data.skill_id);
      const minWage = skill?.minimum_wage_amount ?? 15;
      return z
        .extend(z.omit(schema, { hourly_rate: true }), {
          hourly_rate: z.number().check(
            z.gte(minWage, `Hourly rate must be at least $${minWage}/hr`),
            z.superRefine(async (wage, ctx) => {
              const { gross_wage: grossWage } = await fetchCosts({
                shift_start_time: shift.startTime.toISOString(),
                shift_end_time: shift.endTime.toISOString(),
                hourly_wage: wage,
                location_id: location.id,
                gigpro_fee_percentage: location.gigpro_fee_percentage,
              });
              if (grossWage == null || isWageValid(grossWage)) return;
              ctx.addIssue({
                code: "custom",
                message: `The calculated total wage ($${grossWage}) exceeds $${MAX_WAGE}.`,
              });
            }),
          ),
        })
        .check(z.superRefine(tipPolicyRefinement(isIncludesTipsEnabled && skill?.tips_required)));
    },
    context: skills,
    defaultValues: {
      no_experience_required: false,
      auto_select_enabled: location.immediate_auto_select_enabled,
      ...template,
    },
  });

  const { processing, error, onSelectFiles, files, uploadedFiles } = useUploadAttachment({
    initialAttachments: template?.attachments,
    onSuccess: (attachments) => {
      form.setValue(
        "attachment_ids",
        attachments.map((a) => a.id),
      );
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const gigPayload = {
      skill_id: values.skill_id,
      no_experience_required: false,
      wage_amount: values.hourly_rate,
      description: values.position_description,
      special_inst: values.private_notes,
      selected_requirement_ids: values.requirements?.map((r) => r.id),
      auto_select_enabled: values.auto_select_enabled ?? undefined,
      tip_policy: values.tip_policy ?? null,
      attachments: values.attachment_ids?.map((id) => ({ id, type: "DEFAULT" as const })) ?? [],
    } satisfies Partial<GigPostPayload>;

    try {
      await validateGig(gigPayload);

      if (postOnly || (!saveTemplate && !template?.id)) {
        onSuccess({ ...values, attachments: uploadedFiles.map((f) => f.attachment) });
        onClose();
        return;
      }
      if (saveTemplate) {
        setTemplateToSave(values);
        setSaveTemplateDialogOpen(true);
        return;
      }
    } catch (error) {
      ApiError.fromThrown<GigPostPayload>(error).setFormErrors(form.setError, {
        wage_amount: "hourly_rate",
        description: "position_description",
        special_inst: "private_notes",
        selected_requirement_ids: "requirements",
      });
      return;
    }
    if (!template?.id) return;
    try {
      const { requirements, ...rest } = values;
      await updateTemplate({
        id: template.id,
        data: {
          ...rest,
          attachment_ids: values.attachment_ids ?? [],
          auto_select_enabled: values.auto_select_enabled ?? undefined,
          requirement_ids: requirements?.map((r) => r.id),
        },
      });

      invalidateTemplates();
      onSuccess({
        ...values,
        id: template.id,
        attachments: uploadedFiles.map((f) => f.attachment),
      });
      onClose();
    } catch (error) {
      ApiError.fromThrown<GigTemplatePayload>(error).setFormErrors(form.setError);
    }
  };

  const [selectedSkillId, hourlyRate, skillName] = useWatch({
    control: form.control,
    name: ["skill_id", "hourly_rate", "skill_name"],
  });
  React.useEffect(() => {
    if (!selectedSkillId || !skills || selectedSkillId === template?.skill_id) return;

    const defaults = getDefaults({ skillId: selectedSkillId, skills, shift, location });
    // The tips answer is about the shift, not the skill: carry it across the reset. Whether it's required
    // follows the new skill — the skill-aware resolver re-checks it on the next validation.
    form.reset(defaults && { ...defaults, tip_policy: form.getValues("tip_policy") });
  }, [selectedSkillId, form, shift, location, skills, template?.skill_id]);

  const tipsRequired = !!skills.find(({ id }) => id === selectedSkillId)?.tips_required;

  const { errors, isSubmitting } = useFormState({ control: form.control });
  const disabled = isSubmitting || processing || (error?.length ?? 0) > 0;
  let title = template ? "Edit Template" : "Request a pro";
  let submitLabel = template?.id ? "Save and Update" : "Request a Pro";
  if (postOnly) {
    title = "Position details";
    submitLabel = "Save";
  }
  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <Form id={formId} form={form} onSubmit={onSubmit} className="flex flex-col gap-3">
        <Select
          control={form.control}
          name="skill_id"
          label="Position"
          placeholder="Select position"
          transform={{ output: Number, input: String }}
        >
          {skills?.map(({ id, name }) => (
            <SelectItem key={id} value={`${id}`} data-testid={`skillentry_${name}`}>
              {name}
            </SelectItem>
          ))}
        </Select>
        <Textarea
          control={form.control}
          name="position_description"
          label="Position Description"
          placeholder="This is what pros will see when applying"
          inputClassName="min-h-[150px] transition-[min-height] focus:min-h-[280px]"
          maxLength={1500}
        />
        {!flags?.hideWage && !!selectedSkillId && hourlyRate != null && (
          <WagePickerContainer
            skillId={selectedSkillId}
            template={{ id: template?.id, locationId: location.id, wage: hourlyRate }}
            value={currentWageOption}
            onValueChange={(value) => {
              form.setValue("hourly_rate", value.wage, { shouldDirty: true, shouldValidate: true });
              setCurrentWageOption(value);
            }}
            recommendedWage={recommendedWage}
            defaultOption={template?.hourly_rate && template.hourly_rate !== recommendedWage ? "custom" : "recommended"}
            reverse={!template?.id}
            error={errors.hourly_rate?.message}
          />
        )}
        <Separator className="mt-1.5 mb-0.5 bg-light-gray" />
        <RequirementsFlagged form={form} />
        <Separator className="my-1 bg-light-gray" />
        <Textarea
          control={form.control}
          name="private_notes"
          label="Private notes"
          data-testid="private-notes-input"
          optional
          placeholder="Add a private note for confirmed pros about this shift such as how to get there, where to park, and or what to bring or expect when they arrive"
          inputClassName="min-h-[50px] transition-[min-height] focus:min-h-[150px]"
          maxLength={255}
          rows={3}
        />
        <InputFile
          className="col-span-4"
          onChange={onSelectFiles}
          value={files}
          error={error}
          loading={processing}
          succeedFiles={uploadedFiles.map((file) => file.file)}
          accept={INPUT_FILE_ACCEPT}
        />
        <AccessoriesRequirementsFlagged form={form} />
        {isIncludesTipsEnabled && !!selectedSkillId && (
          <TipPolicyField control={form.control} name="tip_policy" required={tipsRequired} disabled={disabled} />
        )}
        {!!flags?.showAutoSelect && !isAutoSelectExcludedPosition({ skill: skillName }) && (
          <FormField
            control={form.control}
            name="auto_select_enabled"
            render={({ field }) => {
              const value = field.value ?? true;
              return (
                <Label htmlFor={field.name} className="my-1">
                  <Switch
                    id={field.name}
                    name={field.name}
                    checked={value}
                    disabled={disabled ?? false}
                    onCheckedChange={field.onChange}
                  />
                  {value ? "Auto Match Enabled" : "Enable Auto Match"}
                  <Tooltip content="Allow our algorithm to select applicants on your behalf to fill this position">
                    <InfoOutline className="size-3.5 text-info" />
                  </Tooltip>
                </Label>
              );
            }}
          />
        )}
      </Form>
      <DialogFooter className="shadow-up">
        <div className={postOnly ? "hidden" : "mr-auto flex items-center gap-1"}>
          <Checkbox
            id="save_template"
            checked={saveTemplate}
            onCheckedChange={(checked) => setSaveTemplate(!!checked)}
            disabled={isSubmitting}
          />
          <Label htmlFor="save_template" className="text-normal font-semibold whitespace-nowrap">
            {template?.id ? "Save as new template" : "Save as template"}
          </Label>
        </div>
        <Button size="small" onClick={onClose} disabled={disabled}>
          Cancel
        </Button>
        <Button size="small" variant="contained" type="submit" form={formId} loading={isSubmitting} disabled={disabled}>
          {submitLabel}
        </Button>
      </DialogFooter>
      {templateToSave && (
        <SaveTemplateDialog
          open={saveTemplateDialogOpen}
          onOpenChange={setSaveTemplateDialogOpen}
          location={location}
          template={templateToSave}
          onSuccess={(pro) => {
            onSuccess({ ...pro, attachments: uploadedFiles.map((f) => f.attachment) });
            onClose();
          }}
        />
      )}
    </>
  );
}
